import {
  pxToRem,
  pxTovW,
  theme,
  Slide,
  getLocalStorage,
  useCommonServiceClientContext,
  getDifficultyLevelString,
  deserify,
  Loader,
  IStyles,
  cloneObject,
} from '@geneo2-web/shared-ui';
import {
  Checkbox,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Box } from '@mui/system';
import {
  BloomLevelEnum,
  DifficultyLevelEnum,
} from '@protos/content_management/content.db_pb';
import { useEffect, useState } from 'react';
import { useGlobalContext } from '../../../../../app/Context/GlobalContextProvider';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { setToastInfo } from '../../../../Home/reducer/homeDashboard.slice';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import {
  IQPGSettingsConfig,
  setQPGSettingsConfig,
  setQPGSubsectionData,
  setQPGContent,
  setCurrentQPGTemplateInfo,
} from '../../../reducer/assessmentFlow.slice';
import { getDifficultyLevelFromString } from '../../../Common/ReviewAssessment/functions';
import { getUniqueQuestionIdsFromQpgContent } from '../../../utils/QPGv2.utils';
import {
  DifficutlyLevelQuestions,
  QuestionTypeCount,
} from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { interactionEvent } from 'apps/teacher/src/pages/Auth/auth.events';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { QuestionTypeMarks } from '@protos/learning_management/lms.db_pb';
import { BLOOM_LEVEL_ARRAY } from '../../../utils/assessment.utils';

const styles: IStyles = {
  root: {
    width: { xs: '100%', md: pxTovW(1250), lg: pxTovW(874) },
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    mt: { xs: pxToRem(20), md: pxTovW(40) },
  },
  sliderContainer: {
    width: { xs: '97%', md: '100%' },
    marginY: { xs: pxToRem(20), md: 0 },
  },
  divider: {
    width: '100%',
    height: '1px',
    marginY: { xs: pxToRem(20), md: pxTovW(30) },
  },
  settingsCheckboxContainer: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(40) },
  },
  bloomLevelGrid: {
    // border: '1px solid red',
    // height: { xs: '30px', md: '25px' },
    display: 'flex',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    alignItems: 'center',
    paddingY: pxTovW(10),
  },
  bloomLevelCheckbox: {
    height: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    padding: { xs: pxToRem(5), md: pxTovW(8), lg: pxTovW(5) },
    width: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
  },
  allowAiQuestionsContainer: {
    width: '100%',
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  aiCheckbox: {
    height: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    width: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    padding: { xs: pxToRem(5), md: pxTovW(8), lg: pxTovW(5) },
    borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
  },
};

interface QuestionStats {
  questionTypes?: QuestionTypeCount;
  questionDifficultyLevel?: DifficutlyLevelQuestions;
  questionTypeAndMarks?: QuestionTypeMarks[];
}
interface SliderLevel {
  key: DifficultyLevelEnum;
  value: number;
  label?: string;
}

export const QPGCreationSettings = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isIpadOnly } = useGlobalContext();
  const teacher_id = getLocalStorage('userId');
  const { isOffline } = useDownloadContext();
  const {
    LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const {
    qpg_selected_chapter_data,
    qpg_settings_config,
    qpg_content,
    qpg_selected_section_number,
    qpg_selected_section_index,
    current_qpg_template_info,
  } = deserify(useAppSelector((state) => state.assessment));

  const [availableConfig, setAvailableConfig] = useState<IQPGSettingsConfig>({
    overallDifficultyLevel:
      qpg_settings_config?.overallDifficultyLevel || 'Medium',
    no_ofQuestions_by_types: qpg_settings_config?.no_ofQuestions_by_types,
    no_ofQuestions_by_levels: qpg_settings_config?.no_ofQuestions_by_levels,
    bloomLevels: qpg_settings_config?.bloomLevels || [],
    question_type_marks: qpg_settings_config?.question_type_marks || [],
    allow_ai_questions: qpg_settings_config?.allow_ai_questions || false,
    prompt: qpg_settings_config?.prompt || '',
  });
  const [validationStatus, setValidationStatus] = useState<
    { type: 'success' | 'error'; heading?: string; message: string } | undefined
  >(undefined);

  const [loading, setLoading] = useState<'loading' | 'completed'>('completed');
  const [filterLoading, setFilterLoading] = useState<'loading' | 'completed'>(
    'completed'
  );
  const [questionStats, setQuestionStats] = useState<
    Record<string, QuestionStats>
  >({});

  const getModuleIds = () => {
    return (
      qpg_selected_chapter_data?.chapters
        .flatMap((chapter) => [
          chapter.chapterId,
          ...chapter.topics.map((topic) => topic.topicId),
        ])
        ?.sort((a, b) => a - b) || []
    );
  };

  const [isInitialFetch, setIsInitialFetch] = useState(true);
  const sliderLevels = [
    { key: DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW, value: 0, label: 'Easy' },

    {
      key: DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM,
      value: 50,
      label: 'Medium',
    },

    {
      key: DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH,
      value: 100,
      label: 'Hard',
    },
  ];
  const [sliderValue, setSliderValue] = useState<SliderLevel>({
    key: getDifficultyLevelFromString(
      qpg_settings_config?.overallDifficultyLevel || 'Medium'
    ),
    value:
      sliderLevels.find(
        (e) =>
          e.key ===
          getDifficultyLevelFromString(
            qpg_settings_config?.overallDifficultyLevel || 'Medium'
          )
      )?.value || 0,
    label: qpg_settings_config?.overallDifficultyLevel || 'Medium',
  });
  const selectedBloomLevelsString = qpg_settings_config?.bloomLevels?.join(',');

  // Allow all bloom levels if AI Questions
  const filteredBloomlevelArray = qpg_settings_config?.allow_ai_questions
    ? BLOOM_LEVEL_ARRAY
    : BLOOM_LEVEL_ARRAY.filter((bloomLevel) =>
        availableConfig?.bloomLevels?.includes(bloomLevel.typeKey)
      );

  // --------------------------------------------------------------

  const fetchModuleQuestionStatsInitial = async () => {
    try {
      setLoading('loading');
      let allModuleIds = getModuleIds();
      if (!allModuleIds || allModuleIds.length === 0) {
        return;
      }
      const initialBloomLevels =
        qpg_settings_config?.bloomLevels?.sort((a, b) => a - b) || [];
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getModuleQuestionStats(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            moduleIds: allModuleIds,
            qIdsToBeIgnored: getUniqueQuestionIdsFromQpgContent({
              qpgContent: qpg_content,
              sectionNumbersToIgnore:
                qpg_selected_section_number !== undefined
                  ? [qpg_selected_section_number]
                  : [],
            }),
          }
        );
      if (response.data) {
        setQuestionStats((prev) => ({
          ...prev,
          [initialBloomLevels.join(',')]: {
            questionTypes: response.data?.questionTypes,
            questionDifficultyLevel: response.data?.questionDifficultyLevel,
            questionTypeAndMarks: response.data?.questionTypeAndMarks,
          },
        }));
        setAvailableConfig({
          overallDifficultyLevel: getDifficultyLevelString(
            response.data.avgDifficultyLevel
          ),
          no_ofQuestions_by_types: response.data.questionTypes,
          no_ofQuestions_by_levels: response.data.questionDifficultyLevel,
          bloomLevels: response.data.bloomLevels,
          question_type_marks: response.data.questionTypeAndMarks,
        });
        dispatch(
          setQPGSettingsConfig({
            ...qpg_settings_config,
            overallDifficultyLevel:
              qpg_settings_config?.overallDifficultyLevel ??
              getDifficultyLevelString(response.data.avgDifficultyLevel),
            no_ofQuestions_by_types: response.data.questionTypes,
            no_ofQuestions_by_levels: response.data.questionDifficultyLevel,
            bloomLevels:
              qpg_settings_config?.bloomLevels ?? response.data.bloomLevels,
            question_type_marks: response.data.questionTypeAndMarks,
          })
        );
        if (!qpg_settings_config || !qpg_settings_config?.bloomLevels) {
          setIsInitialFetch(false);
        }
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while fetching question stats',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      console.log(err);
      dispatch(
        setToastInfo({
          label: 'Error while fetching question stats',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('completed');
    }
  };

  // --------------------------------------------------------------

  const updateNoOfQuestionByType = async () => {
    if (isInitialFetch || filterLoading === 'loading') return;
    const selectedBloomLevels =
      qpg_settings_config?.bloomLevels?.sort((a, b) => a - b) || [];
    const availableBloomLevels =
      availableConfig.bloomLevels?.sort((a, b) => a - b) || [];
    const allAvailableIncluded = availableBloomLevels.every((level) =>
      selectedBloomLevels.includes(level)
    );
    const bloomLevelsToSend = allAvailableIncluded ? [] : selectedBloomLevels;
    const existingBloomStats = questionStats[bloomLevelsToSend.join(',')];
    if (existingBloomStats) {
      dispatch(
        setQPGSettingsConfig({
          ...qpg_settings_config,
          no_ofQuestions_by_types: existingBloomStats.questionTypes,
          no_ofQuestions_by_levels: existingBloomStats.questionDifficultyLevel,
          question_type_marks: existingBloomStats.questionTypeAndMarks,
        })
      );
      return;
    }

    try {
      setFilterLoading('loading');
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getModuleQuestionStats(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            moduleIds: getModuleIds(),
            bloomLevels: bloomLevelsToSend,
            qIdsToBeIgnored: getUniqueQuestionIdsFromQpgContent({
              qpgContent: qpg_content,
              sectionNumbersToIgnore:
                qpg_selected_section_number !== undefined
                  ? [qpg_selected_section_number]
                  : [],
            }),
          }
        );
      if (response.data) {
        setQuestionStats((prev) => ({
          ...prev,
          [bloomLevelsToSend.join(',')]: {
            questionTypes: response.data?.questionTypes,
            questionTypeAndMarks: response.data?.questionTypeAndMarks,
            questionDifficultyLevel: response.data?.questionDifficultyLevel,
          },
        }));
        dispatch(
          setQPGSettingsConfig({
            ...qpg_settings_config,
            no_ofQuestions_by_types: response.data.questionTypes,
            question_type_marks: response.data.questionTypeAndMarks,
            no_ofQuestions_by_levels: response.data.questionDifficultyLevel,
          })
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setFilterLoading('completed');
    }
  };

  // --------------------------------------------------------------

  useEffect(() => {
    // if ai is unselected, change back to original bloom levels
    if (!qpg_settings_config?.allow_ai_questions) {
      dispatch(
        setQPGSettingsConfig({
          ...qpg_settings_config,
          bloomLevels: qpg_settings_config?.bloomLevels?.filter((bloomLevel) =>
            availableConfig?.bloomLevels?.includes(bloomLevel)
          ),
        })
      );
    }
  }, [qpg_settings_config?.allow_ai_questions]);

  useEffect(() => {
    updateNoOfQuestionByType();
  }, [selectedBloomLevelsString]);

  useEffect(() => {
    fetchModuleQuestionStatsInitial();
  }, [qpg_selected_chapter_data?.chapters?.length]);

  // --------------------------------------------------------------

  // if (loading === 'loading') return <Loader />;

  return (
    <Box sx={styles.root}>
      {(loading === 'loading' || filterLoading === 'loading') && (
        <Loader
          sx={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh' }}
        />
      )}

      <Typography
        variant="h2"
        sx={{ marginY: { xs: pxToRem(20), md: pxTovW(40) } }}
      >
        <span style={{ color: '#007CDC' }}>Step 2: </span>Now set your paper
        level configuration settings
      </Typography>

      <Box sx={styles.sliderContainer}>
        <Slide
          sliderLevels={sliderLevels}
          titleText="Overall Difficulty"
          isDisable={false}
          minLevel={0}
          maxLevel={100}
          step={50}
          slideValue={sliderValue}
          SliderOnChangeValue={async (val: SliderLevel) => {
            setSliderValue(val);
            dispatch(
              setQPGSettingsConfig({
                ...qpg_settings_config,
                overallDifficultyLevel: val.label || 'Medium',
              })
            );
          }}
          onChangeCommitted={(event, value) => {
            const val = value as number;
            const selectedLevel = sliderLevels.find(
              (level) => level.value === val
            );
            if (!selectedLevel) return;
            interactionEvent({
              context: 'Paper_level_settings',
              name:
                selectedLevel.key === DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW
                  ? 'EASY'
                  : selectedLevel.key ===
                    DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM
                  ? 'MEDIUM'
                  : 'HARD',
              pathSegments,
              isOffline,
            });
          }}
        />
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.settingsCheckboxContainer}>
        {filteredBloomlevelArray.length > 0 && (
          <>
            <Typography variant="h3" fontWeight="bold">
              Bloom Level
            </Typography>
            <Grid
              container
              spacing={2}
              columns={16}
              sx={{ padding: 0, width: '100%' }}
            >
              {filteredBloomlevelArray.map((bloomLevel, index) => (
                <Grid item xs={6} md={4} key={index} sx={styles.bloomLevelGrid}>
                  <Checkbox
                    checked={
                      !!qpg_settings_config?.bloomLevels?.find(
                        (e) => e === bloomLevel.typeKey
                      )
                    }
                    onChange={async (e) => {
                      if (e.target.checked) {
                        dispatch(
                          setQPGSettingsConfig({
                            ...qpg_settings_config,
                            bloomLevels: [
                              ...(qpg_settings_config?.bloomLevels || []),
                              bloomLevel.typeKey,
                            ],
                          })
                        );
                      } else {
                        dispatch(
                          setQPGSettingsConfig({
                            ...qpg_settings_config,
                            bloomLevels: (
                              qpg_settings_config?.bloomLevels || []
                            ).filter((level) => level !== bloomLevel.typeKey),
                          })
                        );
                      }
                      await interactionEvent({
                        context: 'Bloom_level',
                        name: bloomLevel.title
                          .toUpperCase()
                          .replace(/\s+/g, '_'),
                        pathSegments: pathSegments,
                        isOffline: isOffline,
                      });
                    }}
                    sx={styles.bloomLevelCheckbox}
                  />
                  <Typography variant={isIpadOnly ? 'h3' : 'h4'}>
                    {bloomLevel.title}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* <Box sx={styles.allowAiQuestionsContainer}>
          <Checkbox
            sx={styles.aiCheckbox}
            checked={qpg_settings_config?.generate_sample_answer_paper || false}
            onChange={async (e) => {
              dispatch(
                setQPGSettingsConfig({
                  ...qpg_settings_config,
                  generate_sample_answer_paper: e.target.checked,
                })
              );
              await interactionEvent({
                context: 'Generate_sample_answer_paper',
                name: e.target.checked ? 'SELECTED' : 'UNSELECTED',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          />
          <Typography variant="h3" fontWeight="bold">
            Generate Sample Answer Paper
          </Typography>
        </Box> */}

        <Box sx={styles.allowAiQuestionsContainer}>
          <Checkbox
            sx={styles.aiCheckbox}
            checked={qpg_settings_config?.allow_ai_questions || false}
            onChange={async (e) => {
              const nextAllowAi = e.target.checked;
              dispatch(
                setQPGSettingsConfig({
                  ...qpg_settings_config,
                  allow_ai_questions: nextAllowAi,
                })
              );

              // AI mode changes available question types — reset current section
              // subsections so Choose question type starts fresh.
              dispatch(setQPGSubsectionData([]));

              const sectionIndex = qpg_selected_section_index ?? 0;
              if (qpg_content?.multiSubjectSections?.[sectionIndex]) {
                const sections = [...qpg_content.multiSubjectSections];
                const selectedSection = cloneObject(
                  qpg_content.multiSubjectSections[sectionIndex]
                );
                selectedSection.multiSubjectSubSections = [];
                sections[sectionIndex] = selectedSection;
                const updatedContent = cloneObject(qpg_content);
                updatedContent.multiSubjectSections = sections;
                dispatch(setQPGContent(updatedContent));
              }

              if (current_qpg_template_info) {
                dispatch(
                  setCurrentQPGTemplateInfo({
                    ...current_qpg_template_info,
                    questionSectionInfo: [],
                  })
                );
              }

              await interactionEvent({
                context: 'Allow_AI_generated_question',
                name: nextAllowAi ? 'SELECTED' : 'UNSELECTED',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
          />
          <Typography variant="h3" fontWeight="bold">
            Allow AI Generated Questions
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
